import * as React from 'react';
import {useEffect, useState, useCallback, useContext} from 'react';
import {ConfigProps, reduxForm} from 'redux-form';
import {FieldWithName, FormInfo, SectionInfo} from '../FormSections/consts';
import {DecoratedComponentClass} from 'redux-form';
import * as _ from 'lodash';
import {ExtendedButtonProps} from '@material-ui/core/Button';
import fieldsToSections from '../FormSections/fieldsToSections';
import * as warning from 'warning';
import Prompt from '../Prompt/Prompt';
import {wrapSubmitFunction} from '../utils/throwSubmissionError';
import {createValidateTypedFields} from '../validation/validators';
import CollapsingWrapper from '../../../shared/components/CollapsingWrapper/CollapsingWrapper';
import withChildrenRefsProvider, {ChildrenRefsContext, ChildrenRefsContextProps} from './withChildrenRefsProvider';

export interface FormContextProviderProps<FormData extends object = {}> {
  formConfig: ConfigProps<FormData>;
  getInitialValues(): Promise<ConfigProps<FormData>['initialValues']>;
  getFormInfo(): FormInfo | Promise<FormInfo>;
  onCreate?(data: FormData, formInfo: FormInfo): Promise<FormData>;
  onUpdate(initialValues: Partial<FormData>, data: Partial<FormData>, formInfo: FormInfo): Promise<FormData>;
}

interface ErrorMeta {
  sectionWithError?: string;
  fieldNameWithError?: string;
  scrollTo?: {y: number};
}

interface ReduxFormInnerProps {
  errorMeta?: ErrorMeta;
  childrenRefs?: ChildrenRefsContextProps['childrenRefs'];
}

export interface FormContextProps extends ReduxFormInnerProps {
  sections: SectionInfo<any>[];
  buttonProps: {
    submit: Partial<ExtendedButtonProps>;
    reset: Partial<ExtendedButtonProps>;
  };
}

export const FormContext = React.createContext<FormContextProps>({errorMeta: {}} as FormContextProps);
const Provider = FormContext.Provider;
export const FormContextConsumer: React.Consumer<FormContextProps> = FormContext.Consumer;

function FormContextProvider<FormData extends object = {}>({
  formConfig,
  children,
  getInitialValues,
  getFormInfo,
  onUpdate,
  onCreate,
}: FormContextProviderProps<FormData> & {children?: React.ReactNode}) {
  const [Component, setComponent] = useState<
    DecoratedComponentClass<FormData, Partial<ConfigProps<FormData> & ReduxFormInnerProps>, string>
  >(null);
  const [formInfo, setFormInfo] = useState<FormInfo>(null);
  const [initialValues, setInitialValues] = useState<Partial<FormData>>(null);
  const [validate, setValidate] = useState<ConfigProps<FormData>['validate']>(null);
  const [errorMeta, setErrorMeta] = useState<ErrorMeta>({});
  const {childrenRefs} = useContext(ChildrenRefsContext);

  const onSubmit = useCallback(
    wrapSubmitFunction(async (data: FormData) => {
      let updatedData: FormData;
      updatedData = await (initialValues[formInfo.id]
        ? onUpdate(initialValues, data, formInfo)
        : onCreate(data, formInfo));
      return setInitialValues({[formInfo.id]: initialValues[formInfo.id], ...updatedData});
    }),
    [onUpdate, initialValues, formInfo, onCreate]
  );

  async function init() {
    const formInfo: FormInfo = await getFormInfo();
    setFormInfo(formInfo);
    const sections: SectionInfo<FormData>[] = fieldsToSections<FormData>(formInfo);
    const handleSubmitFail = errors => {
      if (!_.isEmpty(errors)) {
        const firstErrorFieldName = _.keys(errors)[0];
        const errorField: FieldWithName = _.find(formInfo.fields, {name: firstErrorFieldName}) as any;
        if (errorField) {
          setErrorMeta({sectionWithError: errorField.section, fieldNameWithError: firstErrorFieldName});
        }
      }
    };
    setComponent(
      reduxForm<FormData, ReduxFormInnerProps>({
        ...formConfig,
        enableReinitialize: true,
        onSubmitFail: handleSubmitFail,
      })(({errorMeta, childrenRefs, handleSubmit, pristine, dirty, reset, children}) => {
        if (errorMeta.fieldNameWithError) {
          _.delay(
            () => setErrorMeta({scrollTo: {y: childrenRefs[errorMeta.fieldNameWithError].offsetTop - 50}}),
            CollapsingWrapper.defaultTransitionDuration + 30
          );
        } else if (errorMeta.scrollTo) {
          setErrorMeta({});
        }
        return (
          <Provider
            value={{
              sections,
              buttonProps: {
                reset: {onClick: reset, disabled: pristine},
                submit: {onClick: handleSubmit, disabled: pristine},
              },
              errorMeta,
            }}
          >
            <Prompt when={dirty} />
            {children}
          </Provider>
        );
      })
    );
    const object = await getInitialValues();
    warning(object || onCreate, 'onCreate must be provided when getInitialValues does not return an object');
    setInitialValues(object);
    if (!formConfig.validate) {
      setValidate(() => createValidateTypedFields(formInfo.fields));
    }
  }
  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    init();
  }, []);

  if (!Component) {
    return null;
  }
  return (
    <Component
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={formConfig.validate ? formConfig.validate : validate}
      errorMeta={errorMeta}
      childrenRefs={childrenRefs}
    >
      {children}
    </Component>
  );
}

export default withChildrenRefsProvider(FormContextProvider);
